import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { it as itL } from "date-fns/locale";
import { adminApi, errMsg } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toISODate } from "@/components/site/DatePicker";

const fromISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const tomorrow = () => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1); return d; };

/* Admin: click days to close/reopen them. Fully booked days (confirmed bookings) are shown striped. */
export const AvailabilityDialog = ({ tour, onClose, onSaved }) => {
  const [closed, setClosed] = useState([]);
  const [avail, setAvail] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tour) return;
    setClosed((tour.closed_dates || []).map(fromISO));
    setAvail(null);
    adminApi.get(`/admin/tours/${tour.id}/availability`).then((r) => setAvail(r.data)).catch(() => setAvail({ full: [], remaining: {} }));
  }, [tour]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put(`/admin/tours/${tour.id}/closed-dates`, { dates: closed.map(toISODate) });
      toast.success("Disponibilità aggiornata");
      onSaved(r.data.closed_dates);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const full = (avail?.full || []).map(fromISO);
  const few = Object.keys(avail?.remaining || {}).map(fromISO);

  return (
    <Dialog open={!!tour} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white sm:max-w-2xl rounded-2xl" data-testid="availability-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ocean">Disponibilità</DialogTitle>
          <DialogDescription>{tour?.translations?.it?.title || tour?.translations?.en?.title}: clicca sui giorni per chiuderli o riaprirli. I giorni pieni si calcolano dalle prenotazioni confermate.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          <Calendar
            mode="multiple"
            selected={closed}
            onSelect={(v) => setClosed(v || [])}
            numberOfMonths={2}
            locale={itL}
            disabled={{ before: tomorrow() }}
            modifiers={{ full, few }}
            modifiersClassNames={{ full: "day-full", few: "day-few" }}
            classNames={{ day_selected: "bg-ocean text-ivory hover:bg-ocean hover:text-ivory focus:bg-ocean focus:text-ivory" }}
          />
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-ocean/65" data-testid="availability-legend">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-ocean" />Chiuso da te ({closed.length})</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm day-full" />Completo ({full.length})</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-sea" />Ultimi posti</span>
          <span>Posti per data: {avail?.max_guests ?? "non definiti (conferma manuale)"}</span>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-5 py-3 rounded-xl border border-sand text-sm text-ocean" data-testid="availability-cancel">Annulla</button>
          <button onClick={save} disabled={saving} className="px-6 py-3 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-60 btn-lift" data-testid="availability-save">{saving ? "Salvataggio..." : "Salva date chiuse"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
